import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';

const DOTS_PER_MM = 8;
const DEFAULT_WIDTH_MM = 80;
const DEFAULT_HEIGHT_MM = 200;
const FONT_SIZE = { 1: 13.33, 2: 20, 3: 26.67 };

const escapeXml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const splitTickets = (rawData) => String(rawData || '')
    .split(/PRINT\s+\d+(?:\s*,\s*\d+)?\s*(?:\r?\n|$)/i)
    .map((ticket) => ticket.trim())
    .filter(Boolean);

const ticketSize = (tspl) => {
    const lines = tspl.split(/\r?\n/);
    const sizeLine = lines.find((line) => /^SIZE\s/i.test(line)) || '';
    const sizeMatch = sizeLine.match(/SIZE\s+([\d.]+)\s*mm,\s*([\d.]+)\s*mm/i);
    return {
        widthMm: sizeMatch ? Number(sizeMatch[1]) : DEFAULT_WIDTH_MM,
        heightMm: sizeMatch ? Number(sizeMatch[2]) : DEFAULT_HEIGHT_MM
    };
};

const ticketToPage = (tspl, dimensions = ticketSize(tspl)) => {
    const lines = tspl.split(/\r?\n/);
    const { widthMm, heightMm } = dimensions;
    const widthDots = widthMm * DOTS_PER_MM;
    const heightDots = heightMm * DOTS_PER_MM;
    const svgElements = [];
    const ticketElements = [];

    lines.forEach((line) => {
        const textMatch = line.match(
            /^TEXT\s+(-?\d+),(\d+),"([^"]+)",0,(\d+),(\d+)(?:,(\d+))?,"(.*)"$/
        );
        if (textMatch) {
            const font = textMatch[3];
            const xScale = Number(textMatch[4]);
            const yScale = Number(textMatch[5]);
            const alignment = Number(textMatch[6] || 1);
            const anchor = alignment === 2 ? 'middle' : alignment === 3 ? 'end' : 'start';
            const x = Number(textMatch[1]);
            const y = Number(textMatch[2]);
            const fontSize = (FONT_SIZE[font] || FONT_SIZE[2]) * yScale;
            ticketElements.push({
                type: 'text',
                x,
                y,
                fontSize,
                xScale,
                anchor,
                text: textMatch[7]
            });
            svgElements.push(
                `<text x="${x}" y="${y + fontSize}" text-anchor="${anchor}" `
                + `font-family="Courier New,monospace" font-size="${fontSize}" `
                + 'font-weight="700" '
                + `transform="translate(${x} 0) scale(${xScale} 1) translate(${-x} 0)">`
                + `${escapeXml(textMatch[7])}</text>`
            );
            return;
        }

        const barMatch = line.match(/^BAR\s+(-?\d+),(\d+),(\d+),(\d+)$/);
        if (barMatch) {
            const height = Math.max(1, Number(barMatch[4]));
            ticketElements.push({
                type: 'bar',
                x: Number(barMatch[1]),
                y: Number(barMatch[2]),
                width: Number(barMatch[3]),
                height
            });
            svgElements.push(
                `<rect x="${barMatch[1]}" y="${barMatch[2]}" width="${barMatch[3]}" `
                + `height="${height}" fill="#000" />`
            );
            return;
        }

        const qrMatch = line.match(/^QRCODE\s+(-?\d+),(\d+),[^,]+,(\d+),[^"]+"(.*)"$/);
        if (qrMatch) {
            const size = Math.max(180, Number(qrMatch[3]) * 32.67);
            const qr = renderToStaticMarkup(React.createElement(QRCodeSVG, {
                value: qrMatch[4], size, level: 'L', includeMargin: false
            }));
            const viewBox = qr.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
            const qrPaths = Array.from(qr.matchAll(/<path[^>]*\sd="([^"]+)"/g));
            ticketElements.push({
                type: 'qr',
                x: Number(qrMatch[1]),
                y: Number(qrMatch[2]),
                moduleSize: Number(qrMatch[3]),
                cells: viewBox ? Number(viewBox[1]) : 33,
                path: qrPaths.length > 0 ? qrPaths[qrPaths.length - 1][1] : ''
            });
            svgElements.push(
                `<g transform="translate(${qrMatch[1]} ${qrMatch[2]})" `
                + `data-module-size="${qrMatch[3]}">${qr}</g>`
            );
        }
    });

    return {
        elements: ticketElements,
        svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${widthMm}mm" height="${heightMm}mm" `
            + `viewBox="0 0 ${widthDots} ${heightDots}">${svgElements.join('')}</svg>`
    };
};

export const buildSystemTicketPages = (rawData) => {
    const tickets = splitTickets(rawData);
    if (tickets.length === 0) throw new Error('No hay contenido para imprimir.');
    return tickets.map((ticket) => {
        const dimensions = ticketSize(ticket);
        return { ...dimensions, ...ticketToPage(ticket, dimensions) };
    });
};

export const buildSystemTicketHtml = (rawData) => {
    const pages = buildSystemTicketPages(rawData);
    const pageWidthMm = Math.max(...pages.map(({ widthMm }) => widthMm));
    const pageHeightMm = Math.max(...pages.map(({ heightMm }) => heightMm));
    return `<!doctype html><html><head><meta charset="utf-8"><title></title><style>
        @media print {
            @page { size: ${pageWidthMm}mm ${pageHeightMm}mm; margin: 0; }
            html, body { width: ${pageWidthMm}mm; margin: 0 !important; padding: 0 !important; }
        }
        html, body { width: ${pageWidthMm}mm; margin: 0; padding: 0; background: #fff; }
        .ticket { margin: 0; padding: 0; overflow: visible; break-inside: avoid; page-break-inside: avoid; }
        .ticket:not(:last-child) { break-after: page; page-break-after: always; }
        .ticket svg { display: block; overflow: visible; }
        .new-page { break-before: page; page-break-before: always; }
    </style></head><body>${pages.map((page, index) =>
        `<section class="ticket ${index > 0 ? 'new-page' : ''}" `
        + `style="width:${page.widthMm}mm;height:${page.heightMm}mm">${page.svg}</section>`).join('')}</body></html>`;
};

export default { buildSystemTicketHtml, buildSystemTicketPages };
